<template>
   <Head>
      <title>{{ $t("meta.password_lost.title") }}</title>
      <meta name="description" :content="$t('meta.password_lost.content')" />
   </Head>
   <ClientOnly>
      <div id="passwordLostInput" class="w-full" data-cy="password-lost-container">
         <transition name="portal-fade" mode="out-in">
            <AuthLayout v-if="state.isCompleted" :heading="$t('authentication.reset_password')">
               <template v-slot:description>
                  <div aria-live="polite">
                     <div v-show="state.showAfterCompleted">
                        <p data-cy="password-lost-email-sent-text" class="mb-6 leading-tight">
                           {{ $t("authentication.reset_link_sent") }}
                        </p>
                        <p data-cy="password-lost-email-sent" class="text-bolder mb-6 text-center text-brand-dark">
                           {{ state.userEmail }}
                        </p>
                        <p class="text-bold text-base leading-tight">
                           {{ $t("authentication.check_inbox_with_instructions") }}
                        </p>
                     </div>
                  </div>

                  <EmailClientLinks class="mt-8" />
               </template>
            </AuthLayout>

            <AuthLayout v-else :heading="$t('authentication.reset_password')">
               <template v-slot:description>
                  {{ $t("authentication.email_for_reset_link") }}
               </template>

               <template v-slot:form>
                  <div class="reset">
                     <SnTextLine
                        id="email"
                        v-model.trim="state.userEmail"
                        type="text"
                        redBorderError
                        iconError
                        :label="$t('validation.email.value')"
                        :placeholder="$t('validation.email.placeholder')"
                        :error="emailHelperData"
                        size="large"
                     />
                  </div>

                  <LoadingButton
                     :is-loading="state.isLoading"
                     class="mt-8 w-full"
                     data-cy="password-lost-button"
                     :text="$t('authentication.request_password_reset')"
                     :styling="{ roundedEdges: 'full', size: 'medium', color: 'brand-dark' }"
                     @click="sendResetLink"
                  />
               </template>

               <template v-slot:bottom>
                  <router-link
                     data-cy="to-login"
                     :to="{ name: 'login', params: { locale: $route.params?.locale } }"
                     class="mx-1 inline-block border-b text-brand-dark"
                  >
                     {{ $t("authentication.remembered_password") }}
                  </router-link>
               </template>
            </AuthLayout>
         </transition>
      </div>
   </ClientOnly>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { Head } from "@unhead/vue/components";
import { useToastKey } from "@/plugins/useToast";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useVuelidate } from "@vuelidate/core";
import { required, email, helpers } from "@vuelidate/validators";
import UsersApi from "@/data/api-layer/usersApi";
import SnTextLine from "@sano-ui-library/src/ui/Form/SnTextLine/SnTextLine.vue";

interface State {
   isLoading: boolean;
   isCompleted: boolean;
   showAfterCompleted: boolean;
   userEmail: string | null;
}

const state = reactive<State>({
   isLoading: false,
   isCompleted: false,
   showAfterCompleted: false, // to show the email sent message and communicate with aria-live
   userEmail: null,
});

const { t: $t } = useI18n();
const useToast = inject(useToastKey);

const $v: any = useVuelidate(
   {
      // email validation in this case also checks for '<' and '>' characters
      userEmail: {
         required: helpers.withMessage($t("validation.email.required"), required),
         email: helpers.withMessage($t("validation.email.invalid"), email),
      },
   },
   state
);

// computed
// -----------------------------------------
const emailHelperData = computed(() => {
   if ($v.value.userEmail.$errors.length === 0) return null;
   return $v.value.userEmail.$errors[0].$message;
});

// methods
// -----------------------------------------
const sendResetLink = () => {
   // touch the input to trigger validation
   $v.value.$touch();

   // if there is an error, return
   if ($v.value.$error) return;

   // no errors, send the reset link
   state.isLoading = true;
   UsersApi.resetPassword(state.userEmail)
      .then(() => {
         state.isLoading = false;
         state.isCompleted = true;
         state.showAfterCompleted = true;
      })
      .catch(() => {
         useToast({ text: $t("authentication.wrong_email_2") });
         state.isLoading = false;
      });
};
</script>

<style scoped>
.reset {
   color: var(--grey-80);
}
</style>
